/**
 * Which missions count toward progress when a campaign has choices.
 *
 * - When a decision is made, only the selected branch counts
 * - Non-selected branches are excluded from both numerator and denominator
 * - Before a decision is made, the longest chain (by step count) is the denominator
 */

import { buildChildrenMap, getEffectiveDependencies } from './dependencyGraph';

/**
 * Length of the longest downstream chain from a task.
 * Used when a decision hasn't been made yet to estimate task count.
 *
 * @returns {number} Length of longest chain (including start task)
 */
function calculateLongestChainLength(startTaskId, childrenMap, taskMap, visited = new Set()) {
  if (visited.has(startTaskId)) return 0; // Cycle detected
  visited.add(startTaskId);

  const children = childrenMap.get(startTaskId) || [];
  if (children.length === 0) return 1; // Leaf node

  let maxChildChain = 0;
  for (const { childId } of children) {
    const childTask = taskMap.get(childId);
    if (!childTask) continue;

    const childChain = calculateLongestChainLength(childId, childrenMap, taskMap, new Set(visited));
    maxChildChain = Math.max(maxChildChain, childChain);
  }

  return 1 + maxChildChain;
}

/**
 * Tasks excluded from progress because they sit on a non-selected decision branch.
 * Unmade decisions are not excluded; the caller keeps only the longest chain.
 */
function analyzeDecisionBranches(tasks) {
  const taskMap = new Map(tasks.map(t => [t.id, t]));
  const childrenMap = buildChildrenMap(tasks);
  const excludedTaskIds = new Set();
  const unmadeDecisions = new Map();

  // Specific parent->child edges on non-selected branches.
  // A task is excluded only if ALL of its incoming edges are excluded.
  const excludedEdges = new Set();

  const decisionNodes = tasks.filter(t => t.nodeType === 'decision');

  for (const decision of decisionNodes) {
    const children = childrenMap.get(decision.id) || [];
    const selectedBranch = decision.answer;

    if (selectedBranch) {
      for (const { childId, branch } of children) {
        if (branch && branch !== selectedBranch) {
          excludedEdges.add(`${decision.id}->${childId}`);
        }
      }
    } else {
      const branchLengths = [];

      for (const { childId, branch } of children) {
        if (branch) {
          const chainLength = calculateLongestChainLength(childId, childrenMap, taskMap);
          branchLengths.push({ branch, childId, length: chainLength });
        }
      }

      if (branchLengths.length > 0) {
        branchLengths.sort((a, b) => b.length - a.length);

        unmadeDecisions.set(decision.id, {
          longestBranch: branchLengths[0],
          allBranches: branchLengths,
        });
      }
    }
  }

  if (excludedEdges.size > 0) {
    propagateExclusion(tasks, excludedEdges, excludedTaskIds);
  }

  return { excludedTaskIds, unmadeDecisions };
}

/**
 * A task is excluded if ALL of its incoming dependencies are either an excluded
 * edge or from a task that is itself excluded. One valid incoming path keeps it.
 */
function propagateExclusion(tasks, excludedEdges, excludedSet) {
  let changed = true;

  while (changed) {
    changed = false;

    for (const task of tasks) {
      if (excludedSet.has(task.id)) continue;

      const deps = getEffectiveDependencies(task, tasks);
      if (deps.length === 0) continue;

      const allDepsExcluded = deps.every(({ taskId: parentId }) => {
        if (excludedEdges.has(`${parentId}->${task.id}`)) return true;
        if (excludedSet.has(parentId)) return true;
        return false;
      });

      if (allDepsExcluded) {
        excludedSet.add(task.id);
        changed = true;
      }
    }
  }
}

/** All task IDs downstream of a starting task. */
function collectBranchTasks(startTaskId, childrenMap, resultSet) {
  if (resultSet.has(startTaskId)) return;
  resultSet.add(startTaskId);

  const children = childrenMap.get(startTaskId) || [];
  for (const { childId } of children) {
    collectBranchTasks(childId, childrenMap, resultSet);
  }
}

/**
 * For each unmade decision, count tasks on the longest chain only.
 * Convergence points (reachable from more than one branch) still count.
 */
function calculateEffectiveTaskCount(tasks, unmadeDecisions, excludedTaskIds) {
  const childrenMap = buildChildrenMap(tasks.filter(t => !excludedTaskIds.has(t.id)));

  let effectiveCount = 0;
  const countedTasks = new Set();
  const longestBranchTasks = new Set();
  const shorterBranchTasks = new Set();

  for (const [, { allBranches }] of unmadeDecisions) {
    if (allBranches.length <= 1) continue;

    const longestBranch = allBranches[0];
    collectBranchTasks(longestBranch.childId, childrenMap, longestBranchTasks);

    for (let i = 1; i < allBranches.length; i++) {
      collectBranchTasks(allBranches[i].childId, childrenMap, shorterBranchTasks);
    }
  }

  for (const taskId of longestBranchTasks) {
    shorterBranchTasks.delete(taskId);
  }

  for (const task of tasks) {
    if (excludedTaskIds.has(task.id)) continue;
    if (shorterBranchTasks.has(task.id)) continue;
    effectiveCount++;
    countedTasks.add(task.id);
  }

  return { effectiveCount, countedTaskIds: countedTasks };
}

/**
 * Tasks that count toward progress (non-selected decision branches removed).
 *
 * @returns {{ relevantTasks: Array, effectiveTotal: number, excludedTaskIds: Set, countedTaskIds: Set }}
 */
export function getRelevantTasksForProgress(tasks) {
  if (!tasks || tasks.length === 0) {
    return { relevantTasks: [], effectiveTotal: 0, excludedTaskIds: new Set(), countedTaskIds: new Set() };
  }

  const missionTasks = tasks.filter((t) => t.nodeType !== 'milestone' && t.nodeType !== 'note');
  const { excludedTaskIds, unmadeDecisions } = analyzeDecisionBranches(missionTasks);
  const relevantTasks = missionTasks.filter(t => !excludedTaskIds.has(t.id));

  let effectiveTotal;
  let countedTaskIds;
  if (unmadeDecisions.size > 0) {
    const result = calculateEffectiveTaskCount(missionTasks, unmadeDecisions, excludedTaskIds);
    effectiveTotal = result.effectiveCount;
    countedTaskIds = result.countedTaskIds;
  } else {
    effectiveTotal = relevantTasks.length;
    countedTaskIds = new Set(relevantTasks.map(t => t.id));
  }

  return { relevantTasks, effectiveTotal, excludedTaskIds, countedTaskIds };
}
